import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ArrowLeft, Filter, Download, Trash2, Edit2, 
  Calendar, TrendingUp, Eye, MessageCircle, Search,
  FileText, Fuel, ShoppingBag, Zap, Activity, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { reportsService } from '../services/api';
import { EditPostModal } from './EditPostModal';
import { ReportCard } from './ReportCard';
import { ReportDetailModal } from './ReportDetailModal';

interface MyPostsViewProps {
  onBack: () => void;
  user: any;
}

export function MyPostsView({ onBack, user }: MyPostsViewProps) {
  const [posts, setPosts] = React.useState<any[]>([]);
  const [filteredPosts, setFilteredPosts] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [filterType, setFilterType] = React.useState<string>('all');
  const [sortBy, setSortBy] = React.useState<'newest' | 'oldest' | 'popular'>('newest');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [editingPost, setEditingPost] = React.useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [deletingPostId, setDeletingPostId] = React.useState<string | null>(null);
  const [selectedPost, setSelectedPost] = React.useState<any>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = React.useState(false);

  // Load user's posts
  React.useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setIsLoading(true);
      const { reports } = await reportsService.getMyReports();
      
      // Transform API response to match ReportCard interface (GlobalReport)
      const transformedReports = (reports || []).map((report: any) => ({
        id: report.id,
        userId: report.userId || report.user_id || '',
        userName: report.userName || report.user_name || user?.name || 'You',
        userAvatar: report.userAvatar || report.user_avatar || '',
        userTrustScore: report.userTrustScore || report.user_trust_score || 0,
        type: report.type || report.category || 'other',
        subType: report.subType || report.sub_type || report.subtype || '',
        content: report.description || report.content || '',
        location: report.location || '',
        region: report.region || report.state || '',
        country: report.country || 'Nigeria',
        timestamp: report.timestamp || report.createdAt || report.created_at || new Date().toISOString(),
        likes: report.likes || report.upvotes || 0,
        comments: report.comments || [],
        imageUrl: report.imageUrl || report.image_url || '',
        metadata: report.metadata || {},
        isEdited: report.isEdited || report.is_edited || false,
        syncing: report.syncing || false
      }));
      
      setPosts(transformedReports);
      setFilteredPosts(transformedReports);
      console.log(`✅ Loaded ${transformedReports.length} posts`);
    } catch (error: any) {
      console.error('Failed to load posts:', error);
      toast.error('Failed to load your posts');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter and sort posts
  React.useEffect(() => {
    let result = [...posts];

    // Filter by type
    if (filterType !== 'all') {
      result = result.filter(post => post.type === filterType);
    }

    // Filter by search query
    if (searchQuery) {
      result = result.filter(post => 
        post.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.type?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      } else if (sortBy === 'oldest') {
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      } else { // popular
        return (b.likes || 0) - (a.likes || 0);
      }
    });

    setFilteredPosts(result);
  }, [posts, filterType, sortBy, searchQuery]);

  const handleEdit = (post: any) => {
    setEditingPost(post);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedData: any) => {
    try {
      // Structure the update data properly with metadata
      const updatePayload: any = {
        description: updatedData.description,
        location: updatedData.location,
      };

      // Add metadata if price, status, or station are provided
      if (updatedData.price || updatedData.status || updatedData.station) {
        updatePayload.metadata = {
          ...(editingPost.metadata || {}),
          ...(updatedData.price && { price: updatedData.price }),
          ...(updatedData.status && { status: updatedData.status }),
          ...(updatedData.station && { station: updatedData.station }),
        };
      }

      // Add title if provided
      if (updatedData.title) {
        updatePayload.title = updatedData.title;
      }

      console.log('💾 Saving edit with payload:', updatePayload);
      
      const { report } = await reportsService.updateReport(editingPost.id, updatePayload);
      
      // Transform the updated report to match our local state structure
      const transformedReport = {
        ...editingPost,
        content: report.description || report.content || updatedData.description,
        location: report.location || updatedData.location,
        metadata: report.metadata || updatePayload.metadata || {},
        isEdited: true,
      };
      
      // Update local state
      setPosts(prev => prev.map(p => p.id === editingPost.id ? transformedReport : p));
      
      toast.success('Post updated successfully!', {
        description: 'Your changes have been saved'
      });
      
      setIsEditModalOpen(false);
      setEditingPost(null);
    } catch (error: any) {
      console.error('Failed to update post:', error);
      toast.error('Failed to update post', {
        description: error.message
      });
    }
  };

  const handleDelete = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingPostId(postId);
      await reportsService.deleteReport(postId);
      
      // Remove from local state
      setPosts(prev => prev.filter(p => p.id !== postId));
      
      toast.success('Post deleted successfully');
    } catch (error: any) {
      toast.error('Failed to delete post', {
        description: error.message
      });
    } finally {
      setDeletingPostId(null);
    }
  };

  const handleExport = () => {
    try {
      // Convert posts to CSV
      const headers = ['Date', 'Type', 'Location', 'Description', 'Upvotes'];
      const rows = filteredPosts.map(post => [
        new Date(post.timestamp).toLocaleString(),
        post.type,
        post.location,
        post.content?.replace(/,/g, ';'), // Escape commas
        post.likes || 0
      ]);

      const csv = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sentlo-posts-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Posts exported!', {
        description: 'Your post history has been downloaded as CSV'
      });
    } catch (error) {
      toast.error('Failed to export posts');
    }
  };

  // Transform post data to match ReportDetailModal interface
  const transformPostForModal = (post: any) => {
    if (!post) return null;
    
    return {
      id: post.id,
      category: post.type || 'other',
      location: post.location || 'Unknown',
      state: post.region || post.state || '',
      lga: post.lga || '',
      description: post.content || post.description || '',
      timestamp: post.timestamp || post.createdAt || new Date().toISOString(),
      reporter: post.userName || user?.name || 'You',
      reporterScore: post.userTrustScore || 0,
      reporterRole: 'user' as const,
      upvotes: post.likes || post.upvotes || 0,
      imageUrl: post.imageUrl || '',
      isOfficial: false
    };
  };

  // Calculate stats
  const totalPosts = posts.length;
  const totalUpvotes = posts.reduce((sum, p) => sum + (p.likes || 0), 0);
  const mostPopularType = posts.reduce((acc: any, post) => {
    acc[post.type] = (acc[post.type] || 0) + 1;
    return acc;
  }, {});
  const topType = Object.entries(mostPopularType).sort((a: any, b: any) => b[1] - a[1])[0]?.[0] || 'N/A';

  const typeIcons: any = {
    traffic: Activity,
    fuel: Fuel,
    market: ShoppingBag,
    power: Zap,
    event: Calendar,
    flood: AlertTriangle
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        className="min-h-screen bg-gray-50 pb-20"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white sticky top-0 z-30 shadow-lg">
          <div className="px-4 pt-12 pb-6">
            <button
              onClick={onBack}
              className="mb-4 p-2 rounded-xl bg-white/20 hover:bg-white/30 active:scale-95 transition-all backdrop-blur-sm"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            
            <h1 className="text-2xl font-black mb-2">My Posts</h1>
            <p className="text-white/90 text-sm">Manage your community contributions</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 px-4 pb-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center">
              <div className="text-2xl font-black mb-1">{totalPosts}</div>
              <div className="text-xs text-white/80 font-bold">Total Posts</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center">
              <div className="text-2xl font-black mb-1">{totalUpvotes}</div>
              <div className="text-xs text-white/80 font-bold">Verifications</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center">
              <div className="text-2xl font-black mb-1 capitalize">{topType}</div>
              <div className="text-xs text-white/80 font-bold">Top Type</div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white border-b border-gray-100 px-4 py-4 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your posts..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-green-500 focus:ring-0 outline-none transition-colors"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {['all', 'traffic', 'fuel', 'market', 'power', 'event', 'flood'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-4 py-2 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
                  filterType === type
                    ? 'bg-green-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          {/* Sort & Export */}
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="flex-1 px-4 py-2 rounded-xl border-2 border-gray-200 focus:border-green-500 focus:ring-0 outline-none font-bold text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="popular">Most Popular</option>
            </select>
            <button
              onClick={handleExport}
              className="px-4 py-2 rounded-xl bg-blue-500 text-white font-bold flex items-center gap-2 hover:bg-blue-600 active:scale-95 transition-all shadow-lg"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Posts List */}
        <div className="px-4 py-4">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-full border-4 border-green-500 border-t-transparent animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading your posts...</p>
            </div>
          ) : filteredPosts.length > 0 ? (
            <div className="space-y-4">
              {filteredPosts.map((post) => (
                <div 
                  key={post.id} 
                  className="relative cursor-pointer"
                  onClick={() => {
                    console.log('📝 Opening post detail:', post);
                    setSelectedPost(post);
                    setIsDetailModalOpen(true);
                  }}
                >
                  <ReportCard
                    report={post}
                    onLike={() => {}}
                    onComment={() => {}}
                    onGift={() => {}}
                    isGiftingEnabled={false}
                  />
                  
                  {/* Edit/Delete Overlay */}
                  <div className="absolute top-2 right-2 flex gap-2 z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent opening detail modal
                        handleEdit(post);
                      }}
                      className="p-2 rounded-xl bg-white border-2 border-gray-200 text-blue-600 hover:bg-blue-50 active:scale-95 transition-all shadow-md"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent opening detail modal
                        handleDelete(post.id);
                      }}
                      disabled={deletingPostId === post.id}
                      className="p-2 rounded-xl bg-white border-2 border-gray-200 text-red-600 hover:bg-red-50 active:scale-95 transition-all shadow-md disabled:opacity-50"
                    >
                      {deletingPostId === post.id ? (
                        <div className="w-4 h-4 rounded-full border-2 border-red-600 border-t-transparent animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* Edited Badge */}
                  {post.isEdited && (
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-1 rounded-lg bg-blue-100 text-blue-700 text-xs font-bold">
                        Edited
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">No Posts Found</h3>
              <p className="text-sm text-gray-600">
                {searchQuery || filterType !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Start contributing to your community!'}
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Edit Modal */}
      <EditPostModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingPost(null);
        }}
        post={editingPost}
        onSave={handleSaveEdit}
      />

      {/* Detail Modal */}
      <ReportDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedPost(null);
        }}
        report={transformPostForModal(selectedPost)}
      />
    </>
  );
}